import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { firstValueFrom } from 'rxjs';
import { User } from '../../models/user';
import { SessionContextService } from '../session-context/session-context.service';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly oktaAuth = inject(OKTA_AUTH);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly sessionContext = inject(SessionContextService);

  readonly currentUser = signal<User | null>(null);

  async initialize(): Promise<void> {
    try {
      // Only exchange the authorization code when we're actually returning
      // from Okta; on a normal load/refresh this is a no-op.
      if (this.oktaAuth.isLoginRedirect()) {
        await this.oktaAuth.handleLoginRedirect();
        // A genuine login (not a page refresh): start a fresh audit session id.
        this.sessionContext.rotate();
      }

      if (!(await this.oktaAuth.isAuthenticated())) {
        // Anonymous visitor: leave them where they are (e.g. the login page).
        // The guard sends them to Okta if they try to reach a protected route.
        this.currentUser.set(null);
        return;
      }

      const claims = await this.oktaAuth.getUser();
      const user: User = {
        externalId: claims.sub as string,
        firstName: (claims['given_name'] as string) ?? '',
        lastName: (claims['family_name'] as string) ?? '',
      };

      const signedInUser = await firstValueFrom(this.userService.getOrInsertUser(user));
      this.currentUser.set(signedInUser);

      if (!signedInUser.isActive) {
        this.router.navigate(['/unauthorized']);
        return;
      }

      // Land verified staff on the dashboard at the root route; anywhere else is a
      // deep link we should leave alone.
      const path = window.location.pathname;
      if (path === '/login' || path === '/login/callback') {
        this.router.navigate(['/']);
      }
    } catch {
      this.currentUser.set(null);
    }
  }

  login(): void {
    this.oktaAuth.signInWithRedirect();
  }

  logout(): void {
    this.currentUser.set(null);
    this.sessionContext.clear();
    this.oktaAuth.signOut();
  }
}
